import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { YMaps, Map, Polygon } from "@pbe/react-yandex-maps";
import {
  X,
  MapPin,
  Hash,
  Building2,
  DoorOpen,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { QARSHI_BOUNDARY_COORDS, isPointInPolygon } from "./qarshiCoords";
// import process from "process";

// const YANDEX_API_KEY = process.env.YANDEX_API_KEY || "92688ac7-af1a-4e61-8646-1d9cc6cde872";
const YANDEX_API_KEY = "92688ac7-af1a-4e61-8646-1d9cc6cde872";

// Qarshi markazi [latitude, longitude]
const qarshiCenter: [number, number] = [38.839824, 65.792779];

const worldOuterCoords = [
  [85, -180],
  [85, 180],
  [-85, 180],
  [-85, -180],
];

// Debounce
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Koordinatadan manzil olish (butun O'zbekiston bo'ylab)
async function setCoordsWithAddress(
  ymaps: any,
  coords: [number, number],
  setPosition: (pos: [number, number]) => void,
  setAddress: (address: {
    region: string;
    city: string;
    street: string;
    home: string;
    display_name: string;
    delivery_type: "city" | "district" | "";
  }) => void,
  t: any
) {
  const [lat, lng] = coords;
  setPosition(coords);

  try {
    const res = await ymaps.geocode(coords, { results: 1 });
    const obj = res.geoObjects.get(0);

    if (!obj) {
      toast.error(t("address.no_result") || "Manzil ma'lumotlari topilmadi");
      setAddress({
        region: "",
        city: "",
        street: "",
        home: "",
        display_name: `(${lat.toFixed(6)}, ${lng.toFixed(6)})`,
        delivery_type: "",
      });
      return;
    }

    const adminAreas = obj.getAdministrativeAreas
      ? obj.getAdministrativeAreas()
      : [];
    const addressLine = obj.getAddressLine
      ? obj.getAddressLine()
      : `(${lat.toFixed(6)}, ${lng.toFixed(6)})`;

    // Komponentlardan olish
    const components =
      obj.properties.get(
        "metaDataProperty.GeocoderMetaData.Address.Components"
      ) || [];

    const streetComponent = components.find((c: any) => c.kind === "street");
    const homeComponent = components.find((c: any) => c.kind === "house");
    const cityComponent = components.find((c: any) => c.kind === "locality"); // Shahar
    const areaComponent = components.find((c: any) => c.kind === "area"); // Tuman

    const street = streetComponent ? streetComponent.name : "";
    const home = homeComponent ? homeComponent.name : "";
    const city = cityComponent ? cityComponent.name : "";
    const area = areaComponent ? areaComponent.name : "";

    // Butun O'zbekiston bo'ylab: delivery type'ni mavjud bo'lsa to'ldiramiz
    let deliveryType: "city" | "district" | "" = "";
    if (city) {
      deliveryType = "city";
    } else if (area) {
      deliveryType = "district";
    } else {
      // Fallback: manzil mavjud, lekin tip aniqlanmadi
      deliveryType = "city";
    }

    const address = {
      region: adminAreas[0] || "",
      city: city || area || "",
      street: street || "",
      home: home || "",
      display_name: addressLine,
      delivery_type: deliveryType,
    };

    setAddress(address);
  } catch (error) {
    console.error("Geocode error:", error);
    toast.error(t("address.geocode_error") || "Geokodlashda xato yuz berdi");
    setAddress({
      region: "",
      city: "",
      street: "",
      home: "",
      display_name: `(${lat.toFixed(6)}, ${lng.toFixed(6)})`,
      delivery_type: "",
    });
  }
}

// Address interfeysi
interface Address {
  region: string;
  city: string;
  street: string;
  home: string;
  etaj: string;
  latitude: number;
  longitude: number;
  delivery_type: "city" | "district" | "";
  display_name?: string;
}

interface AddressModalProps {
  onClose: () => void;
  onSelect: (address: Address) => void;
  initialAddress?: Address | null;
}

export default function AddressModal({
  onClose,
  onSelect,
  initialAddress,
}: AddressModalProps) {
  const { t } = useTranslation();
  const [ymapsInstance, setYmapsInstance] = useState<any>(null);
  const [mapInstance, setMapInstance] = useState<any>(null);
  const [selectedCoords, setSelectedCoords] = useState<[number, number] | null>(
    initialAddress ? [initialAddress.latitude, initialAddress.longitude] : null
  );
  const [selectedAddress, setSelectedAddress] = useState({
    region: initialAddress?.region || "",
    city: initialAddress?.city || "",
    street: initialAddress?.street || "",
    home: initialAddress?.home || "",
    delivery_type: initialAddress?.delivery_type || "",
    display_name: initialAddress?.display_name || "",
  });
  const [floorNumber, setFloorNumber] = useState(initialAddress?.etaj || "");
  const [entranceNumber, setEntranceNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Har doim eng so'nggi qiymatlarni saqlaydigan reflar — xarita
  // hodisalari (bir marta ro'yxatdan o'tadigan instanceRef ichida)
  // eskirgan closure'ga tayanmasligi uchun
  const ymapsRef = useRef<any>(null);
  const selectedCoordsRef = useRef<[number, number] | null>(selectedCoords);
  const tRef = useRef(t);

  useEffect(() => {
    ymapsRef.current = ymapsInstance;
  }, [ymapsInstance]);
  useEffect(() => {
    selectedCoordsRef.current = selectedCoords;
  }, [selectedCoords]);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  // Xarita markazi uchun manzilni geokodlaydigan debounce qilingan funksiya.
  // Faqat bitta marta yaratiladi va har doim reflar orqali eng yangi
  // qiymatlarni o'qiydi.
  const resolveCenterRef = useRef(
    debounce(async (coords: [number, number]) => {
      if (!ymapsRef.current) return;

      setIsLoading(true);
      setSelectedCoords(coords);

      await setCoordsWithAddress(
        ymapsRef.current,
        coords,
        setSelectedCoords,
        setSelectedAddress,
        tRef.current
      );

      setIsLoading(false);
    }, 350)
  );

  useEffect(() => {
    if (!ymapsInstance || !mapInstance || selectedCoords) return;

    const detectLocation = () => {
      ymapsInstance.geolocation
        .get({
          provider: "auto",
          enableHighAccuracy: true,
          timeout: 10000,
        })
        .then((res: any) => {
          const coords = res.geoObjects.get(0).geometry.getCoordinates();

          if (isPointInPolygon(coords, QARSHI_BOUNDARY_COORDS)) {
            // 16-zoom bilan markazlashtirish, manzil "actionend" orqali aniqlanadi
            mapInstance.setCenter(coords, 16, { duration: 800 });
          } else {
            // Agar Qarshi tashqarisida bo'lsa, Qarshi markaziga yo'naltiramiz
            mapInstance.setCenter(qarshiCenter, 14, { duration: 800 });
          }
        })
        .catch((err: any) => {
          console.warn("GPS topilmadi, browser geolocation ishlatilmoqda", err);

          ymapsInstance.geolocation
            .get({ provider: "browser" })
            .then((res: any) => {
              const coords = res.geoObjects.get(0).geometry.getCoordinates();
              if (isPointInPolygon(coords, QARSHI_BOUNDARY_COORDS)) {
                mapInstance.setCenter(coords, 16, { duration: 800 });
              } else {
                mapInstance.setCenter(qarshiCenter, 14, { duration: 800 });
              }
            })
            .catch(() => {
              toast.error(t("address.location_error") || "Joylashuv aniqlanmadi");
            });
        });
    };

    detectLocation();
  }, [ymapsInstance, mapInstance, selectedCoords, t]);

  // Xarita bosilganda shu nuqtani markazga suradi — manzil "actionend"da aniqlanadi
  const handleMapClick = useCallback(
    (e: any) => {
      const coords = e.get("coords") as [number, number];
      if (mapInstance) {
        mapInstance.setCenter(coords, mapInstance.getZoom(), { duration: 400 });
      }
    },
    [mapInstance]
  );

  // Foydalanuvchi xaritani surganda (yoki dastur markazni o'zgartirganda) boshlanadi
  const handleActionBegin = useCallback(() => {
    setIsDragging(true);
  }, []);

  // Surish/harakat tugagach markazdagi nuqta uchun manzil avtomatik aniqlanadi
  const handleActionEnd = useCallback(() => {
    setIsDragging(false);
    if (!mapInstance) return;

    const coords = mapInstance.getCenter() as [number, number];

    if (!isPointInPolygon(coords, QARSHI_BOUNDARY_COORDS)) {
      toast.error("Hozirda Qarshi shahri bo‘yicha faoliyat yuritilmoqdamiz.");
      const fallback = selectedCoordsRef.current || qarshiCenter;
      mapInstance.setCenter(fallback, mapInstance.getZoom(), { duration: 300 });
      return;
    }

    resolveCenterRef.current(coords);
  }, [mapInstance]);

  const initialCenter = selectedCoords || qarshiCenter;

  const handleSave = () => {
    if (
      selectedCoords &&
      selectedAddress &&
      selectedAddress.delivery_type &&
      !isLoading
    ) {
      const address: Address = {
        region: selectedAddress.region,
        city: selectedAddress.city,
        street: selectedAddress.street,
        home: selectedAddress.home,
        etaj: floorNumber,
        latitude: selectedCoords[0],
        longitude: selectedCoords[1],
        display_name: selectedAddress.display_name,
        delivery_type: selectedAddress.delivery_type as
          | ""
          | "city"
          | "district",
      };
      onSelect(address);
      onClose();
    } else {
      toast.error(t("address.select_required") || "Iltimos, manzilni tanlang");
    }
  };

  // Escape tugmasi bosilganda modal yopiladi
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isLoading) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, isLoading]);

  const isConfirmed = Boolean(selectedAddress.delivery_type);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-[2px] sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="address-modal-title"
    >
      {/* Backdrop bosilganda yopiladi */}
      <div
        className="absolute inset-0"
        onClick={() => !isLoading && onClose()}
      />

      <div className="relative z-10 flex w-full sm:max-w-[600px] max-h-[92vh] sm:max-h-[85vh] flex-col overflow-hidden rounded-t-3xl sm:rounded-2xl bg-white shadow-2xl mb-14 md:mb-0">
        {/* Drag handle - mobil bottom-sheet uchun */}
        <div className="flex justify-center pt-2 sm:hidden">
          <span className="h-1.5 w-10 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-gradient text-white">
              <MapPin size={18} />
            </span>
            <div className="min-w-0">
              <h2
                id="address-modal-title"
                className="truncate text-base font-semibold text-gray-900 sm:text-lg"
              >
                {isLoading
                  ? t("address.loading")
                  : selectedAddress.display_name || t("address.select_address")}
              </h2>
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
                {isConfirmed ? (
                  <>
                    <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
                    <span className="truncate">
                      {t("address.in_delivery_zone") || "Yetkazib berish hududida"}
                    </span>
                  </>
                ) : (
                  t("address.map_hint") || "Manzilni belgilash uchun xaritani suring"
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40"
            disabled={isLoading}
            aria-label={t("cancel") || "Bekor qilish"}
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
          <div className="relative overflow-hidden rounded-xl border border-gray-200">
            <YMaps
              query={{
                apikey: YANDEX_API_KEY,
                lang: "uz_UZ",
                load: "package.full",
              }}
            >
              <Map
                modules={["geocode", "geolocation"]}
                onLoad={(ymaps: any) => setYmapsInstance(ymaps)}
                onClick={handleMapClick}
                onActionBegin={handleActionBegin}
                onActionEnd={handleActionEnd}
                style={{ width: "100%", height: "100%" }}
                className="!h-[250px] sm:!h-[300px]"
                options={{
                  suppressMapOpenBlock: true,
                  copyrightLogoVisible: false,
                  trafficControl: false,
                }}
                defaultState={{
                  center: initialCenter,
                  zoom: 14,
                  controls: ["zoomControl", "geolocationControl"],
                }}
                instanceRef={(map: any) => {
                  if (map && !mapInstance) {
                    setMapInstance(map);

                    const geoControl = map.controls.get("geolocationControl");
                    if (geoControl) {
                      geoControl.options.set({
                        float: "right",
                        position: { bottom: 16, right: 10 },
                        
                      });

                      // To'g'ri hodisa: "click" emas, "locationchange"
                      // Manzil markazga suriladi va "actionend" orqali avtomatik aniqlanadi
                      geoControl.events.add("locationchange", (e: any) => {
                        const coords = e.get("position") as [number, number];

                        if (isPointInPolygon(coords, QARSHI_BOUNDARY_COORDS)) {
                          map.setCenter(coords, 16, { duration: 600 });
                        } else {
                          toast.error("Sizning joylashuvingiz Qarshi shahri hududida emas!");
                        }
                      });
                    }
                  }
                }}
              >
                <Polygon
                  geometry={[worldOuterCoords, QARSHI_BOUNDARY_COORDS]}
                  options={{
                    fillColor: "#ff000022", // Qizil yarimshaffof fon (tashqari hududlar)
                    strokeColor: "#ff0000", // Qizil chegara chizig'i
                    strokeWidth: 2,
                    strokeOpacity: 0.6,
                    fillOpacity: 0.25,
                    interactivityModel: "default#silent", // Bosilganda click mapga o'tib ketishi uchun
                  }}
                />
              </Map>
            </YMaps>

            {/* Markazda qotib turadigan pin — xarita suriladi, pin joyida qoladi */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-full">
              <div
                className={`flex flex-col items-center transition-transform duration-150 ease-out ${
                  isDragging ? "-translate-y-2.5" : "translate-y-0"
                }`}
              >
                <MapPin
                  size={34}
                  className="text-[#0095b6] drop-shadow-md"
                  fill="#e6f7fb"
                  strokeWidth={1.75}
                />
                <span
                  className={`-mt-1 h-1.5 w-1.5 rounded-full bg-black/30 blur-[1px] transition-transform duration-150 ease-out ${
                    isDragging ? "scale-75 opacity-40" : "scale-100 opacity-70"
                  }`}
                />
              </div>
            </div>

            {/* Geokodlash paytida xarita ustida yengil overlay */}
            {isLoading && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/40">
                <span className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-md">
                  <Loader2 size={14} className="animate-spin text-blue-600" />
                  {t("address.loading")}
                </span>
              </div>
            )}
          </div>

          {/* Inputlar */}
          <div className="mt-3 grid grid-cols-1 gap-2.5">
            <div className="relative">
              <MapPin
                size={17}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder={t("address.street") || "Ko'cha"}
                value={selectedAddress.street}
                onChange={(e) =>
                  setSelectedAddress({
                    ...selectedAddress,
                    street: e.target.value,
                  })
                }
                className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-10 pr-3 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/15 disabled:opacity-60"
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-3 gap-2.5">
              <div className="relative">
                <Hash
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder={t("address.home_number") || "Uy"}
                  value={selectedAddress.home}
                  onChange={(e) =>
                    setSelectedAddress({ ...selectedAddress, home: e.target.value })
                  }
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-9 pr-2 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/15 disabled:opacity-60"
                  disabled={isLoading}
                />
              </div>

              <div className="relative">
                <Building2
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder={t("address.floor_number") || "Qavat"}
                  value={floorNumber}
                  onChange={(e) => setFloorNumber(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-9 pr-2 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/15 disabled:opacity-60"
                  disabled={isLoading}
                />
              </div>

              <div className="relative">
                <DoorOpen
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder={t("address.entrance_number") || "Podъezd"}
                  value={entranceNumber}
                  onChange={(e) => setEntranceNumber(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-9 pr-2 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/15 disabled:opacity-60"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tugmalar */}
        <div className="flex gap-3 border-t border-gray-100 px-5 py-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 sm:flex-none sm:px-6"
            disabled={isLoading}
          >
            {t("cancel") || "Bekor qilish"}
          </button>
          <button
            onClick={handleSave}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white transition-all ${
              isConfirmed
                ? "bg-brand-gradient hover:opacity-90 active:scale-[0.99]"
                : "cursor-not-allowed bg-gray-300"
            }`}
            disabled={isLoading || !isConfirmed}
          >
            {isLoading && <Loader2 size={16} className="animate-spin" />}
            {t("save") || "Saqlash"}
          </button>
        </div>
      </div>
    </div>
  );
}
